import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MessageSquare } from 'lucide-react';
import { useTeamChat } from './useTeamChat';
import { useAuth } from '../context/AuthContext';
import { apiGet } from '../utils/api';

const playNotificationTone = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(1046, ctx.currentTime + 0.08);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.3);
    } catch { /* silent fail */ }
};

/**
 * Global hook that listens for team chat events from any page.
 * Shows toast notifications and tracks total unread count for the sidebar badge.
 */
export function useTeamChatNotifications() {
    const { currentUser, canAccess } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const hasAccess = canAccess('team_chat');

    // Map of conversationId → { unread_count, display_name }
    const [conversationUnreads, setConversationUnreads] = useState(new Map());
    const initializedRef = useRef(false);

    const isOnTeamChat = location.pathname.startsWith('/dashboard/team-chat');
    const isOnTeamChatRef = useRef(isOnTeamChat);
    useEffect(() => { isOnTeamChatRef.current = isOnTeamChat; }, [isOnTeamChat]);

    // Fetch initial unread counts
    useEffect(() => {
        if (!hasAccess || initializedRef.current) return;

        async function fetchUnreads() {
            try {
                const response = await apiGet('/conversations');
                if (response.ok) {
                    const data = await response.json();
                    const map = new Map();
                    (data.conversations || []).forEach(c => {
                        map.set(c.id, {
                            unread_count: c.unread_count || 0,
                            display_name: c.display_name || c.title || 'Conversation'
                        });
                    });
                    setConversationUnreads(map);
                    initializedRef.current = true;
                }
            } catch {
                // Non-critical — badge just won't show initial counts
            }
        }

        fetchUnreads();
    }, [hasAccess]);

    // When user navigates to team chat, clear all unreads
    useEffect(() => {
        if (isOnTeamChat && conversationUnreads.size > 0) {
            setConversationUnreads(prev => {
                const next = new Map(prev);
                for (const [id, val] of next) {
                    next.set(id, { ...val, unread_count: 0 });
                }
                return next;
            });
        }
    }, [isOnTeamChat]);

    // Show browser push notification when tab is not focused
    const showBrowserNotification = useCallback((title, body) => {
        try {
            const prefs = JSON.parse(localStorage.getItem('jpro_notification_prefs') || '{}');
            if (prefs.browserNotifications === false) return;
        } catch { /* show by default */ }

        if (Notification.permission !== 'granted' || document.hasFocus()) return;

        const notification = new Notification(title, {
            body,
            icon: '/favicon.ico',
            tag: 'jpro-team-chat',
        });

        notification.onclick = () => {
            window.focus();
            navigate('/dashboard/team-chat');
            notification.close();
        };
    }, [navigate]);

    // Play notification sound (respects user preferences)
    const playSound = useCallback(() => {
        try {
            const prefs = JSON.parse(localStorage.getItem('jpro_notification_prefs') || '{}');
            if (prefs.teamChatSound === false) return;
            playNotificationTone();
        } catch { /* silent fail */ }
    }, []);

    // Show clickable toast that navigates to team chat (respects user preferences)
    const showToast = useCallback((sender, message, duration = 5000) => {
        try {
            const prefs = JSON.parse(localStorage.getItem('jpro_notification_prefs') || '{}');
            if (prefs.teamChatToasts === false) return;
        } catch { /* show toast by default */ }

        toast(
            (t) => (
                <div
                    onClick={() => { toast.dismiss(t.id); navigate('/dashboard/team-chat'); }}
                    style={{ cursor: 'pointer' }}
                    className="flex flex-col gap-0.5"
                >
                    <span className="font-semibold text-sm">{sender}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{message}</span>
                </div>
            ),
            { duration, icon: <MessageSquare size={18} className="text-blue-500" /> }
        );
    }, [navigate]);

    // Handle incoming ActionCable events
    // Uses refs for values that change on navigation to avoid WebSocket reconnection
    const handleGlobalEvent = useCallback((event) => {
        if (event.type === 'new_message' && event.message?.user?.id !== currentUser?.id) {
            // Play sound for incoming messages (even when on team chat page)
            playSound();

            // Don't increment counter if user is on the team chat page
            if (!isOnTeamChatRef.current) {
                setConversationUnreads(prev => {
                    const next = new Map(prev);
                    const existing = next.get(event.conversation_id) || { unread_count: 0, display_name: 'Conversation' };
                    next.set(event.conversation_id, {
                        ...existing,
                        unread_count: existing.unread_count + 1
                    });
                    return next;
                });

                const sender = event.message?.user?.first_name
                    ? `${event.message.user.first_name} ${event.message.user.last_name || ''}`.trim()
                    : event.message?.user?.username || 'Someone';
                const content = (event.message?.content || '').slice(0, 80);
                const preview = `${content}${event.message?.content?.length > 80 ? '...' : ''}`;
                showToast(sender, preview);
                showBrowserNotification(`Team Chat — ${sender}`, preview);
            }
        }

        if (event.type === 'mention' && event.message?.user?.id !== currentUser?.id) {
            const sender = event.message?.user?.first_name
                ? `${event.message.user.first_name} ${event.message.user.last_name || ''}`.trim()
                : event.message?.user?.username || 'Someone';
            const content = (event.message?.content || '').slice(0, 80);
            const preview = `${content}${event.message?.content?.length > 80 ? '...' : ''}`;
            showToast(`${sender} mentioned you`, preview);
            showBrowserNotification(`${sender} mentioned you`, preview);
        }

        if (event.type === 'conversation_updated') {
            const conv = event.conversation;
            if (conv) {
                setConversationUnreads(prev => {
                    const next = new Map(prev);
                    if (!next.has(conv.id)) {
                        // New conversation — user was likely added
                        next.set(conv.id, {
                            unread_count: isOnTeamChatRef.current ? 0 : (conv.unread_count || 0),
                            display_name: conv.display_name || conv.title || 'Conversation'
                        });
                        if (!isOnTeamChatRef.current) {
                            const convName = conv.display_name || conv.title || 'Group Chat';
                            showToast('New Conversation', convName, 6000);
                            showBrowserNotification('JPRO Team Chat', `New conversation: ${convName}`);
                        }
                    } else {
                        // Existing conversation updated — refresh display name
                        const existing = next.get(conv.id);
                        next.set(conv.id, {
                            ...existing,
                            display_name: conv.display_name || conv.title || existing.display_name
                        });
                    }
                    return next;
                });
            }
        }

        if (event.type === 'member_removed' && event.user_id === currentUser?.id) {
            setConversationUnreads(prev => {
                const next = new Map(prev);
                next.delete(event.conversation_id);
                return next;
            });
        }
    }, [currentUser?.id, showToast, showBrowserNotification]);

    // Subscribe to ActionCable — only if user has team_chat access
    useTeamChat(hasAccess ? handleGlobalEvent : null);

    // Derive total unread count
    const totalUnreadCount = useMemo(() => {
        let total = 0;
        for (const { unread_count } of conversationUnreads.values()) {
            total += unread_count;
        }
        return total;
    }, [conversationUnreads]);

    const clearConversationUnread = useCallback((conversationId) => {
        setConversationUnreads(prev => {
            const next = new Map(prev);
            const existing = next.get(conversationId);
            if (existing) {
                next.set(conversationId, { ...existing, unread_count: 0 });
            }
            return next;
        });
    }, []);

    const clearAllUnread = useCallback(() => {
        setConversationUnreads(prev => {
            const next = new Map(prev);
            for (const [id, val] of next) {
                next.set(id, { ...val, unread_count: 0 });
            }
            return next;
        });
    }, []);

    if (!hasAccess) {
        return { totalUnreadCount: 0, clearConversationUnread: () => {}, clearAllUnread: () => {} };
    }

    return { totalUnreadCount, clearConversationUnread, clearAllUnread };
}

export default useTeamChatNotifications;
